// app/components/news/News.tsx
"use client";

import {
  Box,
  Grid,
  Stack,
  Typography,
  Container,
  Divider,
  Link,
  Skeleton,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import PaginationCDV from "@/app/components/PaginationCDV";

interface NewsItem {
  id: string; // จาก Prisma เป็น string
  title_zh: string;
  title_en: string;
  subtitle_zh: string;
  subtitle_en: string;
  coverImageUrl?: string; // ใช้ชื่อนี้แทน img1
  img2?: string;
  date: string; // "YYYY-MM-DD"
  category_zh: string;
  category_en: string;
  descriptionEn: string;
  descriptionZh: string;
  slug_zh: string;
  slug_en: string;
  
  // ✅ fields ใหม่สำหรับ image gallery ตาม device
  coverDesktopUrl?: string;
  coverTabletUrl?: string;
  coverMobileUrl?: string;
}

function getBackgroundImage({ src }: { src?: string }) {
  if (!src) {
    return {
      backgroundColor: "#f5f5f5",
    };
  }
  return {
    backgroundImage: `url(${src})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}

function News({ itemsPerPage = 6, initialRows = [] }: { itemsPerPage?: number; initialRows?: NewsItem[] }) {
  const t = useTranslations("News");
  const locale = useLocale();
  const isEnglish = locale === "en";

  // เพิ่ม useTheme และ useMediaQuery เพื่อตรวจสอบ viewport
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [allNews, setAllNews] = useState<NewsItem[]>(initialRows);
  const [paginatedNews, setPaginatedNews] = useState<NewsItem[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [latestNews, setLatestNews] = useState<NewsItem | null>(initialRows[0] ?? null);
  const [loading, setLoading] = useState<boolean>(initialRows.length === 0);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const categoryOf = (item: NewsItem) =>
    (isEnglish ? item.category_en : item.category_zh) || "";

  // รายชื่อหมวดหมู่ที่มีบทความจริงอยู่ (เรียงตามลำดับที่เจอครั้งแรก)
  const categories = React.useMemo(() => {
    const seen: string[] = [];
    for (const item of allNews) {
      const c = categoryOf(item);
      if (c && !seen.includes(c)) seen.push(c);
    }
    return seen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allNews, isEnglish]);

  const filteredNews = React.useMemo(() => {
    if (selectedCategory === "all") return allNews;
    return allNews.filter((item) => categoryOf(item) === selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allNews, selectedCategory, isEnglish]);

  // ---- ดึงข่าวจาก API ตาม locale ----
  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      setCurrentPage(1);
      setSelectedCategory("all");

      try {
        const res = await fetch(`/api/news?locale=${locale}`, {
          cache: "no-store",
        });

        if (!res.ok) {
          console.error("[News] fetch failed:", res.status);
          setAllNews([]);
          setLatestNews(null);
          setPaginatedNews([]);
          setPageCount(0);
          return;
        }

        const data = await res.json();
        const rows: NewsItem[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : [];

        // sort ล่าสุดก่อน
        const sorted = rows
          .slice()
          .sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          );

        setAllNews(sorted);
      } catch (err) {
        console.error("[News] fetch error:", err);
        setAllNews([]);
        setLatestNews(null);
        setPaginatedNews([]);
        setPageCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [locale]);

  // ---- แบ่งหน้า + เลือกข่าวล่าสุด (ตามหมวดหมู่ที่เลือก) ----
  useEffect(() => {
    if (!filteredNews.length) {
      setLatestNews(null);
      setPaginatedNews([]);
      setPageCount(0);
      return;
    }

    const latest = filteredNews[0] ?? null;
    setLatestNews(latest);

    const newsForPagination = latest ? filteredNews.slice(1) : filteredNews;

    const total = newsForPagination.length;
    const pages = Math.ceil(total / itemsPerPage) || 1;
    setPageCount(pages);

    const safePage = Math.min(currentPage, pages);
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }

    const startIndex = (safePage - 1) * itemsPerPage;
    setPaginatedNews(
      newsForPagination.slice(startIndex, startIndex + itemsPerPage)
    );
  }, [filteredNews, currentPage, itemsPerPage]);

  const handleCategoryChange = (_e: React.SyntheticEvent, value: string) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };

  // ฟังก์ชันเลือก cover image ตาม viewport
  const getCoverImage = (item: NewsItem) => {
    if (isDesktop && item.coverDesktopUrl) {
      return item.coverDesktopUrl;
    } else if (isTablet && item.coverTabletUrl) {
      return item.coverTabletUrl;
    } else if (isMobile && item.coverMobileUrl) {
      return item.coverMobileUrl;
    }
    return item.coverImageUrl;
  };

  // ฟังก์ชันสร้างลิงก์ข่าวตาม locale
  const buildDetailUrl = (item: NewsItem) => {
    const slug = locale === "zh" ? item.slug_zh : item.slug_en;
    const encoded = encodeURIComponent(slug);
    return locale === "zh"
      ? `/zh/news/detail/${encoded}`
      : `/news/detail/${encoded}`;
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  // ---------- Loading UI ----------
  if (loading) {
    return (
      <Box id="news" className="news-section" sx={{
        py: 10
      }}>
        <Container className="news-container" sx={{ pb: 3 }} data-aos="fade-up">
          {/* Skeleton latest news */}
          <Grid
            container
            spacing={0}
            className="latest-news"
            sx={{ mb: 10 }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Skeleton
                variant="rectangular"
                sx={{ width: "100%", height: { xs: 220, md: 320 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: { xs: 3, md: 5 } }}>
                <Skeleton variant="text" sx={{ fontSize: "2rem", mb: 2 }} />
                <Skeleton
                  variant="text"
                  sx={{ fontSize: "1rem", mb: 1 }}
                  width="90%"
                />
                <Skeleton
                  variant="text"
                  sx={{ fontSize: "1rem", mb: 1 }}
                  width="80%"
                />
                <Skeleton
                  variant="text"
                  sx={{ fontSize: "1rem", mb: 3 }}
                  width="70%"
                />
                <Skeleton variant="text" sx={{ fontSize: "0.9rem", width: 80 }} />
                <Box sx={{
                  mt: 3
                }}>
                  <Skeleton variant="rectangular" height={1} />
                  <Skeleton
                    variant="text"
                    sx={{ fontSize: "0.8rem", mt: 1, width: 120 }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Skeleton card list */}
          <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 4 }}
                key={idx}
                className="card-news"
              >
                <Box sx={{ height: "100%" }}>
                  <Grid container spacing={0} className="news">
                    <Grid size={12}>
                      <Skeleton
                        variant="rectangular"
                        sx={{ width: "100%", height: 180 }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Box sx={{ p: 3 }}>
                        <Skeleton
                          variant="text"
                          sx={{ fontSize: "1rem", mb: 1 }}
                          width="90%"
                        />
                        <Skeleton
                          variant="text"
                          sx={{ fontSize: "1rem" }}
                          width="70%"
                        />
                      </Box>
                    </Grid>
                    <Grid
                      size={12}
                      sx={{
                        px: 3,
                        pb: 3,
                        pt: 0
                      }}>
                      <Skeleton variant="rectangular" height={1} />
                      <Skeleton
                        variant="text"
                        sx={{ fontSize: "0.8rem", mt: 1, width: 100 }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  // ---------- Normal UI ----------
  
  return (
    <Box id="news" className="news-section" sx={{
      py: 10
    }}>
      <Container className="news-container" sx={{ pb: 3 }} data-aos="fade-up">
        {/* หมวดหมู่บทความ */}
        {categories.length > 0 && (
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mb: 5,
              borderBottom: "1px solid rgba(255,255,255,0.25)",
              minHeight: 0,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 0,
                py: 1.5,
                color: "rgba(255,255,255,0.75)",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#fff",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#fff",
              },
            }}
          >
            <Tab label={t("allCategories")} value="all" />
            {categories.map((c) => (
              <Tab key={c} label={c} value={c} />
            ))}
          </Tabs>
        )}

        {/* กล่องข่าวล่าสุด */}
        {latestNews && (
          <Link href={buildDetailUrl(latestNews)} underline="none">
            <Grid
              container
              spacing={0}
              className="latest-news"
              sx={{ mb: 10 }}
            >
              <Grid
                size={{ xs: 12, md: 6 }}
                className="latest-news-image"
                sx={getBackgroundImage({ src: getCoverImage(latestNews) })}
              />
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: { xs: 3, md: 5 } }}>
                  <Grid
                    container
                    sx={{
                      mb: 2,
                      flexDirection: "column",
                      justifyContent: "space-between"
                    }}>
                    {categoryOf(latestNews) && (
                      <Typography
                        variant="overline"
                        sx={{ color: "#3d7237", fontWeight: 700, mb: 0.5 }}
                      >
                        {categoryOf(latestNews)}
                      </Typography>
                    )}
                    <Typography
                      variant="h3"
                      component="h3"
                      // ถ้าอยากให้ขึ้นบรรทัดใหม่ตาม <br> ก็ไม่ต้อง set whiteSpace เพิ่มเติม
                      dangerouslySetInnerHTML={{
                        __html: isEnglish ? latestNews.title_en : latestNews.title_zh,
                      }}
                      sx={{
                        mb: 1
                      }}
                    />

                    {/* excerpt / description ตัดบรรทัด + html simple */}
                    <Typography
                      variant="subtitle1"
                      component="div"
                      dangerouslySetInnerHTML={{
                        __html: isEnglish
                          ? latestNews.descriptionEn
                          : latestNews.descriptionZh,
                      }}
                      sx={{
                        mb: 3,
                        display: "-webkit-box",
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        color: "black"
                      }} />

                    <Typography variant="body1" className="read-more">
                      {t("readMore")}
                    </Typography>
                  </Grid>
                  <Grid>
                    <Divider />
                    <Grid container sx={{
                      alignItems: "center"
                    }}>
                      <Grid size={12} className="date">
                        {latestNews.date}
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Link>
        )}

        {/* แสดงข่าวที่เหลือ */}
        {paginatedNews.length > 0 && (
          <Grid container spacing={3}>
            {paginatedNews.map((item) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 4 }}
                key={item.id}
                className="card-news"
                sx={{
                  position: "relative"
                }}
              >
                <Link
                  href={buildDetailUrl(item)}
                  sx={{ display: "block", height: "100%" }}
                  underline="none"
                >
                  <Grid container spacing={0} className="news">
                    <Grid
                      size={12}
                      className="news-image"
                      sx={getBackgroundImage({ src: getCoverImage(item) })}
                    />
                    <Grid size={12}>
                      <Box sx={{ p: 3 }}>
                        {categoryOf(item) && (
                          <Typography
                            variant="overline"
                            sx={{ color: "#3d7237", fontWeight: 700, display: "block" }}
                          >
                            {categoryOf(item)}
                          </Typography>
                        )}
                        <Typography
                          variant="subtitle1"
                          title={
                            isEnglish ? item.title_en : item.title_zh
                          }
                          sx={{
                            mb: "1rem"
                          }}
                        >
                          {isEnglish ? item.title_en : item.title_zh}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 3,
                        pt: 0,
                        width: "100%",
                        mt: "auto"
                      }}>
                      <Grid size={12}>
                        <Divider />
                      </Grid>
                      <Grid size={6} className="date">
                        {item.date}
                      </Grid>
                    </Grid>
                  </Grid>
                </Link>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ไม่มีข่าวให้แสดง */}
        {!loading && filteredNews.length === 0 && (
          <Typography
            sx={{
              textAlign: "center",
              py: 5
            }}>
            {t("no_news_found")}
          </Typography>
        )}

        {/* แสดง Pagination เมื่อมีหลายหน้า */}
        {pageCount > 1 && (
          <Stack
            spacing={2}
            sx={{ mt: 4, display: "flex", alignItems: "flex-end" }}
          >
            <PaginationCDV
              count={pageCount}
              page={currentPage}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
            />
          </Stack>
        )}
      </Container>
    </Box>
  );
}

export default News;